import { useEffect, type ReactNode } from "react";
import { cn } from "@/lib/utils";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: "sm" | "md" | "lg" | "xl";
  /** When true, Escape / overlay / X will not close the modal. */
  preventClose?: boolean;
}

const SIZE = {
  sm: "max-w-md",
  md: "max-w-xl",
  lg: "max-w-3xl",
  xl: "max-w-5xl",
};

export default function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = "md",
  preventClose = false,
}: ModalProps) {
  useEffect(() => {
    if (!open || preventClose) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, preventClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-foreground-950/50 p-0 sm:items-center sm:p-4">
      <button
        type="button"
        aria-label="Close dialog"
        className="absolute inset-0 h-full w-full cursor-default"
        onClick={() => {
          if (!preventClose) onClose();
        }}
      />
      <div
        role="dialog"
        aria-modal="true"
        className={cn(
          "relative z-10 w-full animate-scale-in rounded-t-lg border border-background-200 bg-background-50 sm:rounded-lg",
          SIZE[size],
        )}
      >
        {(title || description) && (
          <header className="flex items-start justify-between gap-4 border-b border-background-200 px-5 py-4">
            <div>
              {title && (
                <h3 className="font-heading text-base font-semibold text-foreground-950">
                  {title}
                </h3>
              )}
              {description && (
                <p className="mt-0.5 text-xs text-foreground-600 md:text-sm">
                  {description}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={preventClose}
              className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-foreground-500 hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <i className="ri-close-line text-lg" />
            </button>
          </header>
        )}
        <div className="max-h-[70vh] overflow-y-auto scrollbar-slim px-5 py-4">
          {children}
        </div>
        {footer && (
          <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-background-200 px-5 py-4">
            {footer}
          </footer>
        )}
      </div>
    </div>
  );
}